// The signed-in frame (spec/15 §2.1): on a phone a slim top bar and a bottom tab bar of five (Home, Venues, Play,
// Wallet, Account; Passes and Orders sit with the Wallet), on a desktop a side rail with every destination. The
// current place is the link's `aria-current`, never colour alone. Everything past sign-in renders inside this.
//
// The venue pages render in this frame for a signed-in player and in the public frame otherwise (`SiteShell`), so
// the navigation never disappears under a player browsing venues.

import { useTranslation } from '@clavian/i18n/react';
import { Button, RouteTransition, SegmentedControl, SideNav, SideNavItem, TabBar, TabBarItem, useToast } from '@clavian/ui-web';
import { useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useCallback, type ReactNode } from 'react';

import { useApi } from '../api/client';
import { useLiveSession, useSession } from '../api/queries';
import { useProblem } from '../api/use-problem';
import { clock, useCountdown } from '../lib/format';
import { ConsentBanner } from './ConsentBanner';
import { Wordmark } from './Mark';
import { NavIcon, type NavIconName } from './nav-icons';
import { PublicFrame } from './PublicFrame';
import { useThemeChoice, type ThemeChoice } from './theme';

interface NavItem {
  readonly to: '/home' | '/venues' | '/play' | '/wallet' | '/passes' | '/orders' | '/account';
  readonly labelKey: string;
  readonly icon: NavIconName;
  /** On the phone's bottom bar (five at most). */
  readonly tab: boolean;
  /** Other paths this item is the current place for (the wallet's own pages). */
  readonly also?: readonly string[];
  /** Pages that belong to this item on every frame, phone and rail alike (claiming a guest pass is under Passes). */
  readonly pages?: readonly string[];
}

const NAV: readonly NavItem[] = [
  { to: '/home', labelKey: 'nav.home', icon: 'home', tab: true },
  { to: '/play', labelKey: 'nav.play', icon: 'play', tab: true },
  { to: '/venues', labelKey: 'nav.venues', icon: 'venues', tab: true },
  { to: '/wallet', labelKey: 'nav.wallet', icon: 'wallet', tab: true, also: ['/passes', '/guest-pass', '/orders'] },
  { to: '/passes', labelKey: 'nav.passes', icon: 'passes', tab: false, pages: ['/guest-pass'] },
  { to: '/orders', labelKey: 'nav.orders', icon: 'orders', tab: false },
  { to: '/account', labelKey: 'nav.account', icon: 'account', tab: true },
];

/** Sign out, forget every cached answer, and return to the landing. The one place the portal does it. */
export function useSignOut(): () => Promise<void> {
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useCallback(async () => {
    try {
      await api.signOut();
    } finally {
      queryClient.clear();
      await navigate({ to: '/' });
    }
  }, [api, queryClient, navigate]);
}

/** Whether `path` is under `prefix` (`/orders/sal_…` is under `/orders`). */
const under = (path: string, prefix: string): boolean => path === prefix || path.startsWith(`${prefix}/`);

function isCurrent(item: NavItem, path: string, onTabBar: boolean): boolean {
  if (under(path, item.to) || (item.pages ?? []).some((page) => under(path, page))) return true;
  // On the phone the Wallet tab stands for its passes and orders too; the side rail lists them on their own.
  return onTabBar && (item.also ?? []).some((other) => under(path, other));
}

export function ThemeSwitch(): ReactNode {
  const { t } = useTranslation('player');
  const { choice, setChoice } = useThemeChoice();
  return (
    <SegmentedControl<ThemeChoice>
      label={t('nav.theme.label')}
      size="sm"
      block
      value={choice}
      onValueChange={setChoice}
      options={[
        { value: 'system', label: t('nav.theme.system') },
        { value: 'light', label: t('nav.theme.light') },
        { value: 'dark', label: t('nav.theme.dark') },
      ]}
    />
  );
}

/** A small "Live 1:02:15" chip in the phone's top bar while a session runs, straight to Play. */
function LiveChip(): ReactNode {
  const { t } = useTranslation('player');
  const live = useLiveSession().data?.live;
  const running = live !== undefined && (live.session.status === 'active' || live.session.status === 'grace');
  const left = useCountdown(live?.session.timeLeftSeconds, live?.session.serverTime, running);
  if (live === undefined || left === undefined) return null;
  return (
    <Link to="/play" className="pw-livechip" data-status={live.session.status}>
      <span className="pw-livechip__dot" aria-hidden="true" />
      <span>{t('nav.live')}</span>
      <span className="pw-livechip__time clav-num">{clock(left)}</span>
    </Link>
  );
}

/** The signed-in chrome around any page. */
export function AppChrome({ children }: { readonly children: ReactNode }): ReactNode {
  const { t } = useTranslation('player');
  const path = useRouterState({ select: (state) => state.location.pathname });
  const signOut = useSignOut();
  const problem = useProblem();
  const { show } = useToast();

  return (
    <div className="pw-app">
      <a className="pw-skip" href="#pw-main">
        {t('app.skipToContent')}
      </a>

      <aside className="pw-rail">
        <SideNav
          label={t('nav.primary')}
          header={
            <Link to="/home" className="pw-rail__brand" aria-label="Clavian">
              <Wordmark size={26} />
            </Link>
          }
          footer={
            <>
              <ThemeSwitch />
              <Button
                variant="ghost"
                icon={<NavIcon name="signout" size={18} />}
                onClick={() => void signOut().catch((error: unknown) => show({ title: problem.text(error, { fallback: t('account.signOutError') }), tone: 'danger' }))}
              >
                {t('nav.signOut')}
              </Button>
            </>
          }
        >
          {NAV.map((item) => (
            <SideNavItem key={item.to} icon={<NavIcon name={item.icon} />}>
              <Link to={item.to} aria-current={isCurrent(item, path, false) ? 'page' : undefined}>
                {t(item.labelKey)}
              </Link>
            </SideNavItem>
          ))}
        </SideNav>
      </aside>

      <header className="pw-mobilebar">
        <Link to="/home" className="pw-mobilebar__brand" aria-label="Clavian">
          <Wordmark size={24} />
        </Link>
        <LiveChip />
      </header>

      <div className="pw-content">
        <ConsentBanner />
        <main className="pw-main" id="pw-main" tabIndex={-1}>
          {/* A page arrives with a short fade and rise; the same path keeps its page (ui-web RouteTransition). */}
          <RouteTransition routeKey={path}>{children}</RouteTransition>
        </main>
      </div>

      <TabBar label={t('nav.primary')} className="pw-tabbar">
        {NAV.filter((item) => item.tab).map((item) => (
          <TabBarItem key={item.to} icon={<NavIcon name={item.icon} />}>
            <Link to={item.to} aria-current={isCurrent(item, path, true) ? 'page' : undefined}>
              {t(item.labelKey)}
            </Link>
          </TabBarItem>
        ))}
      </TabBar>
    </div>
  );
}

/** The signed-in layout route. */
export function AppShell(): ReactNode {
  return (
    <AppChrome>
      <Outlet />
    </AppChrome>
  );
}

/** The venue pages: the signed-in chrome for a player, the public frame for a visitor. */
export function SiteShell(): ReactNode {
  const session = useSession();
  if (session.data != null) {
    return (
      <AppChrome>
        <Outlet />
      </AppChrome>
    );
  }
  return (
    <PublicFrame>
      <Outlet />
    </PublicFrame>
  );
}
